import { ReactNode, MouseEvent, ReactElement, createElement } from 'react';
import { Message } from '@messageformat/react';
import { ListItem, useLocale } from '@op/opds.opux';
import { IconProps } from '@op/opds.opux-icons';
import { useHref, useLinkClickHandler } from 'react-router';
import { IconCheckmark } from '@op/opds.opux-icons/misc';
import { useIsMobile } from '../util/mediaQuery';
import {
  analyticsActionGroup,
  analyticsPhaseName,
  courseSelectionActionName,
  sendAnalyticsAction,
} from '../util/analytics';
import { CourseNumber } from '../interfaces/CoursePage';
import routeNames from '../messages/routes';
import styles from './LandingPageListItem.module.scss';

interface ListItemProps {
  msg: string;
  completed?: boolean;
  course?: CourseNumber;
  iconContent: ReactNode;
}

const IconSmallCheckmark = (props: IconProps): JSX.Element =>
  createElement(IconCheckmark, { ...props, size: 'small', color: 'default' });

const IconInvisible = (): JSX.Element => <div style={{ display: 'none' }} />;

const LandingPageListItem = ({
  msg,
  completed = false,
  course,
  iconContent,
}: ListItemProps): ReactElement => {
  const isMobile = useIsMobile();
  const locale = useLocale();
  const { courses } = routeNames[locale];
  const itemClassName = completed ? `${styles.item} ${styles.completed}` : styles.item;
  const coursePath = `${courses}/${course}`;
  const linkClickHandler = useLinkClickHandler(coursePath);
  const href = useHref(coursePath); // just to force <a> tag rendering for ListItem

  if (isMobile && !course) {
    return (
      <ListItem>
        <Message id={msg} />
      </ListItem>
    );
  }

  const isLink = typeof course === 'number';
  const handleOnClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (isLink) {
      sendAnalyticsAction(
        courseSelectionActionName(course),
        analyticsActionGroup.CLICK,
        analyticsPhaseName.COURSES,
      );
      linkClickHandler(event);
    }
  };

  return (
    <ListItem
      className={itemClassName}
      controlIcon={completed ? IconSmallCheckmark : IconInvisible}
      // ListItem renders as button or anchor if onClick or href is defined, respectively.
      onClick={isLink ? handleOnClick : undefined}
      href={isLink ? href : undefined}
    >
      <div className={styles.body}>
        <div className={styles.circle}>{iconContent}</div>
        <div>
          <Message id={msg} />
        </div>
      </div>
    </ListItem>
  );
};

export default LandingPageListItem;
